import { useTranslationHelper } from "../translationHelper.js";
import { defineNuxtPlugin, useRuntimeConfig } from "#app";
import { useRoute, useRouter, useCookie, useState } from "#imports";
const i18nHelper = useTranslationHelper();
const isDev = process.env.NODE_ENV !== "production";
function interpolate(template, params) {
  let result = template;
  for (const key in params) {
    result = result.split(`{${key}}`).join(String(params[key]));
  }
  return result;
}
function getCurrentLocale(route, i18nConfig, hashLocale) {
  if (i18nConfig.hashMode && hashLocale) {
    return hashLocale;
  }
  return (route.params?.locale ?? i18nConfig.defaultLocale).toString();
}
function getRouteName(route, locale) {
  return (route?.name ?? "").toString().replace("localized-", "").replace(new RegExp(`-${locale}$`), "");
}
function switchLocaleRoute(fromLocale, toLocale, route, router, i18nConfig, i18nRouteParams) {
  const routeName = getRouteName(route, fromLocale);
  if (router.hasRoute(`localized-${routeName}-${toLocale}`)) {
    const newParams2 = { ...route.params, ...i18nRouteParams?.[toLocale] };
    newParams2.locale = toLocale;
    if (i18nConfig.hashMode) {
      const userLocaleCookie = useCookie("hash-locale");
      userLocaleCookie.value = toLocale;
    }
    return {
      name: `localized-${routeName}-${toLocale}`,
      params: newParams2
    };
  }
  const newRouteName = toLocale !== i18nConfig.defaultLocale || i18nConfig.includeDefaultLocaleRoute ? `localized-${routeName}` : routeName;
  const newParams = { ...route.params, ...i18nRouteParams?.[toLocale] };
  delete newParams.locale;
  if (toLocale !== i18nConfig.defaultLocale || i18nConfig.includeDefaultLocaleRoute) {
    newParams.locale = toLocale;
  }
  if (i18nConfig.hashMode) {
    const userLocaleCookie = useCookie("hash-locale");
    userLocaleCookie.value = toLocale;
  }
  return { name: newRouteName, params: newParams };
}
function switchLocale(fromLocale, toLocale, route, router, i18nConfig, i18nRouteParams) {
  const checkLocale = i18nConfig.locales?.find((l) => l.code === toLocale);
  if (!checkLocale) {
    console.warn(`Locale ${toLocale} is not available`);
    return Promise.reject(`Locale ${toLocale} is not available`);
  }
  const switchedRoute = switchLocaleRoute(
    fromLocale,
    toLocale,
    route,
    router,
    i18nConfig,
    i18nRouteParams
  );
  return router.push(switchedRoute);
}
function getLocalizedRoute(to, router, route, i18nConfig, locale, hashLocale) {
  const currentLocale = locale || getCurrentLocale(route, i18nConfig, hashLocale);
  const selectRoute = router.resolve(to);
  const routeName = getRouteName(selectRoute, currentLocale);
  const resolveParams = (to2) => {
    const params = typeof to2 === "object" && "params" in to2 && typeof to2.params === "object" ? { ...to2.params } : {};
    if (typeof to2 === "string") {
      const resolved = router.resolve(to2);
      if (resolved && resolved.params) {
        Object.assign(params, resolved.params);
      }
    }
    return params;
  };
  if (router.hasRoute(`localized-${routeName}-${currentLocale}`)) {
    const newParams2 = resolveParams(selectRoute);
    newParams2.locale = currentLocale;
    return router.resolve({
      name: `localized-${routeName}-${currentLocale}`,
      params: newParams2,
      query: selectRoute.query,
      hash: selectRoute.hash
    });
  }
  const newRouteName = currentLocale !== i18nConfig.defaultLocale || i18nConfig.includeDefaultLocaleRoute ? `localized-${routeName}` : routeName;
  if (!router.hasRoute(newRouteName)) {
    const newParams2 = resolveParams(to);
    delete newParams2.locale;
    return router.resolve({
      name: routeName,
      params: newParams2,
      query: selectRoute.query,
      hash: selectRoute.hash
    });
  }
  const newParams = resolveParams(to);
  delete newParams.locale;
  if (currentLocale !== i18nConfig.defaultLocale || i18nConfig.includeDefaultLocaleRoute) {
    newParams.locale = currentLocale;
  }
  return router.resolve({
    name: newRouteName,
    params: newParams,
    query: selectRoute.query,
    hash: selectRoute.hash
  });
}
function formatNumber(value, locale, options) {
  return new Intl.NumberFormat(locale, options).format(value);
}
function formatDate(value, locale, options) {
  return new Intl.DateTimeFormat(locale, options).format(new Date(value));
}
export default defineNuxtPlugin(async (nuxtApp) => {
  if (!nuxtApp.payload.data.translations) {
    nuxtApp.payload.data.translations = {};
  }
  const config = useRuntimeConfig();
  const i18nConfig = config.public.i18nConfig;
  const plural = new Function("return " + i18nConfig.plural.toString())();
  const apiBaseUrl = i18nConfig.apiBaseUrl ?? "_locales";
  const loadTranslationsIfNeeded = async (locale, routeName) => {
    try {
      if (!i18nHelper.hasPageTranslation(locale, routeName)) {
        let fRouteName = routeName;
        if (i18nConfig.routesLocaleLinks && i18nConfig.routesLocaleLinks[fRouteName]) {
          fRouteName = i18nConfig.routesLocaleLinks[fRouteName];
        }
        const data = await $fetch(`/${apiBaseUrl}/${fRouteName}/${locale}/data.json?v=${i18nConfig.dateBuild}`, { baseURL: i18nConfig.baseURL });
        await i18nHelper.loadPageTranslations(locale, routeName, data ?? {});
      }
    } catch (_error) {
    }
  };
  useRouter().beforeEach(async (to, from, next) => {
    const hashLocale2 = i18nConfig.hashMode ? nuxtApp.runWithContext(() => (useCookie("hash-locale").value ?? i18nConfig.defaultLocale).toString()).toString() : null;
    const locale = getCurrentLocale(to, i18nConfig, hashLocale2);
    if (!i18nHelper.hasGeneralTranslation(locale)) {
      const data = await $fetch(`/${apiBaseUrl}/general/${locale}/data.json?v=${i18nConfig.dateBuild}`, { baseURL: i18nConfig.baseURL });
      await i18nHelper.loadTranslations(locale, data ?? {});
    }
    if (!i18nConfig.disablePageLocales) {
      const routeName = getRouteName(to, locale);
      await loadTranslationsIfNeeded(locale, routeName);
    }
    await nuxtApp.callHook("i18n:register", (translations, selectedLocale) => {
      const routeName = getRouteName(to, locale);
      i18nHelper.mergeTranslation(selectedLocale ?? locale, routeName, translations, true);
    }, locale);
    if (next) {
      next();
    }
  });
  const getTranslation = (key, params, defaultValue) => {
    if (!key) return "";
    const hashLocale2 = i18nConfig.hashMode ? (useCookie("hash-locale").value ?? i18nConfig.defaultLocale).toString() : null;
    const locale = getCurrentLocale(useRoute(), i18nConfig, hashLocale2);
    const routeName = getRouteName(useRoute(), locale);
    let value = i18nHelper.getTranslation(locale, routeName, key);
    if (!value) {
      if (isDev && import.meta.client) {
        console.warn(`Not found '${key}' key in '${locale}' locale messages.`);
      }
      value = defaultValue || key;
    }
    return typeof value === "string" && params ? interpolate(value, params) : value;
  };
  let hashLocale = null;
  if (i18nConfig.hashMode) {
    hashLocale = useCookie("hash-locale").value ?? i18nConfig.defaultLocale;
  }
  const i18nRouteParams = useState("i18n-route-params");
  nuxtApp.hook("page:start", () => {
    i18nRouteParams.value = null;
  });
  const provideData = {
    i18n: void 0,
    __micro: true,
    getLocale: () => getCurrentLocale(useRoute(), i18nConfig, hashLocale),
    defaultLocale: () => i18nConfig.defaultLocale,
    getLocales: () => i18nConfig.locales || [],
    getRouteName: (route, locale) => {
      const selectedLocale = locale ?? getCurrentLocale(useRoute(), i18nConfig, hashLocale);
      const selectedRoute = route ?? useRoute();
      return getRouteName(selectedRoute, selectedLocale);
    },
    t: getTranslation,
    ts: (key, params, defaultValue) => {
      const value = getTranslation(key, params, defaultValue);
      return value?.toString() ?? defaultValue ?? key;
    },
    tc: (key, count, defaultValue) => {
      const currentLocale = getCurrentLocale(useRoute(), i18nConfig, hashLocale);
      return plural(key, count, currentLocale, getTranslation) ?? defaultValue ?? key;
    },
    tn: (value, options) => {
      const locale = getCurrentLocale(useRoute(), i18nConfig, hashLocale);
      return formatNumber(value, locale, options);
    },
    td: (value, options) => {
      const locale = getCurrentLocale(useRoute(), i18nConfig, hashLocale);
      return formatDate(value, locale, options);
    },
    has: (key) => {
      return !!getTranslation(key);
    },
    mergeTranslations: (newTranslations) => {
      const route = useRoute();
      const locale = getCurrentLocale(route, i18nConfig, hashLocale);
      const routeName = getRouteName(route, locale);
      i18nHelper.mergeTranslation(locale, routeName, newTranslations);
    },
    switchLocaleRoute: (toLocale) => {
      const router = useRouter();
      const route = useRoute();
      const fromLocale = getCurrentLocale(route, i18nConfig, hashLocale);
      if (i18nConfig.hashMode) {
        hashLocale = toLocale;
      }
      return switchLocaleRoute(fromLocale, toLocale, route, router, i18nConfig, i18nRouteParams.value);
    },
    switchLocalePath: (toLocale) => {
      const router = useRouter();
      const route = useRoute();
      const fromLocale = getCurrentLocale(route, i18nConfig, hashLocale);
      if (i18nConfig.hashMode) {
        hashLocale = toLocale;
      }
      const localeRoute = switchLocaleRoute(fromLocale, toLocale, route, router, i18nConfig, i18nRouteParams.value);
      if (typeof localeRoute === "string") {
        return localeRoute;
      }
      if ("fullPath" in localeRoute) {
        return localeRoute.fullPath;
      }
      return "";
    },
    switchLocale: (toLocale) => {
      const router = useRouter();
      const route = useRoute();
      const fromLocale = getCurrentLocale(route, i18nConfig, hashLocale);
      if (i18nConfig.hashMode) {
        hashLocale = toLocale;
      }
      switchLocale(fromLocale, toLocale, route, router, i18nConfig, i18nRouteParams.value);
    },
    localeRoute: (to, locale) => {
      const router = useRouter();
      const route = useRoute();
      return getLocalizedRoute(to, router, route, i18nConfig, locale, hashLocale);
    },
    localePath: (to, locale) => {
      const router = useRouter();
      const route = useRoute();
      const localeRoute = getLocalizedRoute(to, router, route, i18nConfig, locale, hashLocale);
      if (typeof localeRoute === "string") {
        return localeRoute;
      }
      if ("fullPath" in localeRoute) {
        return localeRoute.fullPath;
      }
      return "";
    },
    setI18nRouteParams: (value) => {
      i18nRouteParams.value = value;
      return i18nRouteParams.value;
    }
  };
  provideData.i18n = provideData;
  return {
    provide: provideData
  };
});
